import log from "loglevel";
import React, { useState, useCallback, memo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as constants from "Constants";
import httpService from "services/httpService";
import { Button } from "react-bootstrap";
import {
  MdMenuBook,
  MdScience,
  // MdInfoOutline, // Commented out - used by About Course which is hidden
  MdCalendarToday,
  MdForum,
  MdMailOutline,
  MdCampaign,
  MdGrade,
  MdInsights,
  MdLeaderboard,
  MdBarChart,
  MdSettings,
  MdPeople,
  MdDescription,
  MdImportExport
} from "react-icons/md";
import { IoAddCircleOutline } from "react-icons/io5";
import dragula from "dragula";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import "./SideBar.scss";

// NavItem with access check before navigation
const NavItem = memo(({ to, children, pageID, as = "Link", icon: Icon, isActive, onBeforeNavigate }) => {
  const navigate = useNavigate();

  const handleClick = async (e) => {
    e.preventDefault();
    // Check access before navigating
    if (onBeforeNavigate) {
      const canNavigate = await onBeforeNavigate();
      if (!canNavigate) return;
    }
    // Proceed with navigation
    const path = typeof to === 'object' ? to.pathname : to;
    navigate(path);
  };

  const content = (
    <div className={`nav-item-content ${isActive ? "active" : ""}`}>
      {Icon && <Icon size={18} className="nav-icon" />}
      <span>{children}</span>
    </div>
  );

  return (
    <li pageID={pageID} className="nav-item">
      <a href={typeof to === 'object' ? to.pathname : to} onClick={handleClick} className="nav-link">
        {content}
      </a>
    </li>
  );
});

const SideBar = ({ courseId, pages, visibility = {}, onBeforeNavigate }) => {
  const location = useLocation();
  const [order, setOrder] = useState(false);
  const [pagesCopy, setPagesCopy] = useState([]);
  const [pageDragulaService, setPageDragulaService] = useState(null);

  // Helper to check if a path is active
  const checkIsActive = useCallback((to) => {
    const path = typeof to === "object" ? to.pathname : to;
    return location.pathname.includes(path);
  }, [location.pathname]);

  const handleAdditionalPageReorder = useCallback((pagesData) => {
    log.log(pagesData);
    if (!order) {
      let tempPagesCopy = [...pagesData];
      let tempIdToPage = {};
      pagesData.forEach((page) => {
        tempIdToPage[page.id] = page;
      });

      const drag = dragula(
        [document.getElementById("additionalPagesV2")],
        { revertOnSpill: true }
      ).on("drop", (el, container) => {
        log.log(container.children);
        tempPagesCopy = Array.from(container.children).map((list) => {
          return tempIdToPage[list.attributes.pageID.nodeValue];
        });
        log.log(tempPagesCopy);
        setPagesCopy(tempPagesCopy);
      });

      setPageDragulaService(drag);
      setOrder(true);
    } else {
      if (pageDragulaService) {
        pageDragulaService.destroy();
      }

      if (pagesCopy && pagesCopy.length > 0) {
        log.log(pagesCopy);
        const playlist = pagesCopy.map((page) => page.id);
        const url = constants.base_url + `/courseware/api/course/${courseId}/reorder_pages/?format=json`;

        const reorderPages = async () => {
          try {
            await httpService.patch(url, { "playlist": JSON.stringify(playlist) });
            displayGlobalMessage('Successfully reordered the pages', 'success');
            setPagesCopy([]);
          } catch (error) {
            log.error(error);
          }
        };

        reorderPages();
      }
      setOrder(false);
    }
  }, [order, pagesCopy, pageDragulaService, courseId]);

  return (
    <div className="sidebar-v2">
      <ul className="nav-list" role="menu" aria-labelledby="dLabel">
        {/* Course Content Section */}
        <li className="nav-section-title">Course Content</li>
        {visibility.multimedia_book !== false && (
          <NavItem
            to={`/courseware/course/${courseId}/content/multimedia`}
            icon={MdMenuBook}
            isActive={checkIsActive(`/courseware/course/${courseId}/content/multimedia`)}
            onBeforeNavigate={onBeforeNavigate}
          >
            Multimedia Book
          </NavItem>
        )}
        {visibility.labs !== false && (
          <NavItem
            to={`/courseware/course/${courseId}/content/labs`}
            icon={MdScience}
            isActive={checkIsActive(`/courseware/course/${courseId}/content/labs`)}
            onBeforeNavigate={onBeforeNavigate}
          >
            Labs
          </NavItem>
        )}
        <hr className="nav-divider" />

        {/* Interactions Section */}
        <li className="nav-section-title">Interactions</li>
        {visibility.schedule !== false && (
          <NavItem
            to={`/courseware/course/${courseId}/interactions/schedule`}
            icon={MdCalendarToday}
            isActive={checkIsActive(`/courseware/course/${courseId}/interactions/schedule`)}
            onBeforeNavigate={onBeforeNavigate}
          >
            Schedule
          </NavItem>
        )}
        {visibility.interaction !== false && (
          <>
            <NavItem
              to={`/courseware/course/${courseId}/interactions/discussion-forum`}
              icon={MdForum}
              isActive={checkIsActive(`/courseware/course/${courseId}/interactions/discussion-forum`)}
              onBeforeNavigate={onBeforeNavigate}
            >
              Discussion Forum
            </NavItem>
            <NavItem
              to={`/courseware/course/${courseId}/interactions/email-notices`}
              icon={MdMailOutline}
              isActive={checkIsActive(`/courseware/course/${courseId}/interactions/email-notices`)}
              onBeforeNavigate={onBeforeNavigate}
            >
              Email Notices
            </NavItem>
          </>
        )}
        {visibility.announcements !== false && (
          <NavItem
            to={`/courseware/course/${courseId}/interactions/announcements`}
            icon={MdCampaign}
            isActive={checkIsActive(`/courseware/course/${courseId}/interactions/announcements`)}
            onBeforeNavigate={onBeforeNavigate}
          >
            Course Announcements
          </NavItem>
        )}

        {/* Performance Section */}
        {visibility.performance !== false && (
          <>
            <hr className="nav-divider" />
            <li className="nav-section-title">Performance</li>
            <NavItem
              to={`/courseware/course/${courseId}/performance/grades`}
              icon={MdGrade}
              isActive={checkIsActive(`/courseware/course/${courseId}/performance/grades`)}
              onBeforeNavigate={onBeforeNavigate}
            >
              Grades
            </NavItem>
            <NavItem
              to={`/courseware/course/${courseId}/performance/leaderboard`}
              icon={MdLeaderboard}
              isActive={checkIsActive(`/courseware/course/${courseId}/performance/leaderboard`)}
              onBeforeNavigate={onBeforeNavigate}
            >
              Leaderboard
            </NavItem>
            <NavItem
              to={`/courseware/course/${courseId}/performance/statistics`}
              icon={MdBarChart}
              isActive={checkIsActive(`/courseware/course/${courseId}/performance/statistics`)}
              onBeforeNavigate={onBeforeNavigate}
            >
              Statistics
            </NavItem>
          </>
        )}

        <hr className="nav-divider" />

        {/* Course Management Section */}
        <li className="nav-section-title">Course Management</li>
        <NavItem
          to={`/courseware/course/${courseId}/management/settings`}
          icon={MdSettings}
          isActive={checkIsActive(`/courseware/course/${courseId}/management/settings`)}
          onBeforeNavigate={onBeforeNavigate}
        >
          Settings
        </NavItem>
        <NavItem
          to={`/courseware/course/${courseId}/management/people`}
          icon={MdPeople}
          isActive={checkIsActive(`/courseware/course/${courseId}/management/people`)}
          onBeforeNavigate={onBeforeNavigate}
        >
          People
        </NavItem>

        {/* Additional Pages Section */}
        {visibility.extra_pages !== false && (
          <>
            <li className="nav-section-title">Additional Pages</li>
            <li className="nav-item nav-actions">
              <Link
                className="nav-link"
                to={`/courseware/course/${courseId}/pages/addpage`}
              >
                <Button
                  variant="primary"
                  className="btn-add-page"
                >
                  <IoAddCircleOutline size={18} />
                  Add Page
                </Button>
              </Link>
              <Button
                variant="light"
                className="btn-reorder"
                onClick={() => handleAdditionalPageReorder(pages)}
              >
                <MdImportExport color="#3f3f3f" size={20} />
                {order ? "Save" : ""}
              </Button>
            </li>
            <div id="additionalPagesV2">
              {pages?.map((page) => {
                const pagePath = `/courseware/course/${courseId}/pages/${page.id}`;
                return (
                  <NavItem
                    pageID={page.id}
                    key={page.id}
                    icon={MdDescription}
                    isActive={checkIsActive(pagePath)}
                    onBeforeNavigate={onBeforeNavigate}
                    to={{
                      pathname: pagePath,
                      state: { page: page },
                    }}
                  >
                    {page.title}
                  </NavItem>
                );
              })}
            </div>
          </>
        )}
      </ul>
    </div>
  );
};

export default React.memo(SideBar);
